// =============================================================================
// VUE VISITEUR — Mobile-first, terrain
// =============================================================================
// Focus : Identifier le client, voir le statut, accéder photos/DAL
// Tap targets >= 44px, stack vertical, gros boutons d'action
// =============================================================================

import {
  ArrowLeft, User, MapPin, Phone, Camera, FileText,
  Calendar, CheckCircle2, Clock, XCircle, RefreshCw,
  ChevronRight, FolderOpen, Shield,
} from "lucide-react";
import { useState } from "react";
import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge";
import { PipelineStepper, getPipelineStep, getPipelineOrder } from "./PipelineStepper";
import { PhotosForm } from "./PhotosForm";
import { DocumentsForm } from "./DocumentsForm";
import { QRCodeDAL } from "./MobileAccess";
import DALForm from "../../../components/tef/DALForm";
import usePipeline from "../../../hooks/usePipeline";
import { formatClientAddress, formatClientFullName } from "../../../utils/clientFormat";

function BigActionButton({ icon: Icon, label, sublabel, color, onClick, testId }) {
  return (
    <button
      onClick={onClick}
      className="w-full flex items-center gap-4 rounded-xl px-5 py-4 transition-all active:scale-[0.98]"
      style={{ background: `${color}10`, border: `1px solid ${color}30`, minHeight: "56px" }}
      data-testid={testId}
    >
      <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${color}20` }}>
        <Icon className="w-5 h-5" style={{ color }} />
      </div>
      <div className="text-left flex-1 min-w-0">
        <p className="text-sm font-medium" style={{ color: "var(--oh-text)" }}>{label}</p>
        {sublabel && <p className="text-xs truncate" style={{ color: "var(--oh-text-muted)" }}>{sublabel}</p>}
      </div>
      <ChevronRight className="w-5 h-5 shrink-0" style={{ color: "var(--oh-text-muted)" }} />
    </button>
  );
}

function StatusChip({ label, ok }) {
  return (
    <div className="flex items-center gap-2 py-1">
      {ok ? <CheckCircle2 className="w-4 h-4 text-[var(--oh-success-text)]" /> : <XCircle className="w-4 h-4" style={{ color: "var(--oh-text-muted)" }} />}
      <span className="text-sm" style={{ color: ok ? "var(--oh-text)" : "var(--oh-text-muted)" }}>{label}</span>
    </div>
  );
}

export function DossierVisiteurView({ dossier, dossierId, tenantId, documents, onBack, onUpdate }) {
  const [activePanel, setActivePanel] = useState(null); // "photos" | "documents" | "dal"
  const { pipeline: pipelineDoc } = usePipeline();

  if (!dossier) return null;

  const client = dossier.client || {};
  const clientNom = formatClientFullName(client) || dossier.client_nom || "—";
  const adresse = formatClientAddress(client) || dossier.infos_logement?.adresse || "";
  const pipelineOrder = getPipelineOrder(dossier.statut, dossier, pipelineDoc);

  const checklist = [
    { label: "Visite effectuée", ok: !!dossier.date_visite },
    { label: "Visite payée", ok: dossier.visite_payee === true },
    { label: "Dossier récupéré", ok: !!dossier.dossier_recupere },
    { label: "Photos ajoutées", ok: pipelineOrder >= 3 },
  ];

  return (
    <div className="space-y-4 max-w-lg mx-auto" data-testid="dossier-visiteur-view">
      {/* Header minimal */}
      <div
        className="sticky top-0 z-40 px-4 py-3 backdrop-blur-xl"
        style={{ background: "var(--oh-bg-elevated)", borderBottom: "1px solid var(--oh-border)" }}
      >
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={onBack} data-testid="visiteur-back-btn" style={{ minWidth: "44px", minHeight: "44px" }}>
            <ArrowLeft className="w-5 h-5" style={{ color: "var(--oh-text-muted)" }} />
          </Button>
          <div className="flex-1 min-w-0">
            <p className="font-medium truncate" style={{ color: "var(--oh-text)", fontFamily: "'IBM Plex Mono', monospace" }}>
              {dossier.référence}
            </p>
          </div>
          <Button variant="ghost" size="sm" onClick={onUpdate} data-testid="visiteur-refresh-btn" style={{ minWidth: "44px", minHeight: "44px" }}>
            <RefreshCw className="w-5 h-5" style={{ color: "var(--oh-text-muted)" }} />
          </Button>
        </div>
      </div>

      {/* Client Card — prominent */}
      <div className="rounded-xl p-5" style={{ background: "var(--oh-bg-elevated)", border: "1px solid var(--oh-border)" }} data-testid="visiteur-client-card">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: "var(--oh-hub-commercial, var(--oh-stat-blue-text))20", border: "2px solid var(--oh-hub-commercial, var(--oh-stat-blue-text))" }}>
            <User className="w-6 h-6" style={{ color: "var(--oh-hub-commercial, var(--oh-stat-blue-text))" }} />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-lg font-medium truncate" style={{ color: "var(--oh-text)" }}>{clientNom}</p>
            {adresse && <p className="text-xs truncate" style={{ color: "var(--oh-text-muted)" }}>{adresse}</p>}
          </div>
        </div>
        {/* Quick contact */}
        <div className="flex gap-2">
          {client.telephone && (
            <a
              href={`tel:${client.telephone}`}
              className="flex-1 flex items-center justify-center gap-2 rounded-lg py-3 text-sm font-medium transition-all active:scale-[0.97]"
              style={{ background: "#3b82f615", border: "1px solid #3b82f630", color: "var(--oh-stat-blue-text)", minHeight: "48px" }}
              data-testid="visiteur-call-btn"
            >
              <Phone className="w-4 h-4" />
              Appeler
            </a>
          )}
          {adresse && (
            <a
              href={`https://maps.google.com/?q=${encodeURIComponent(adresse)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 flex items-center justify-center gap-2 rounded-lg py-3 text-sm font-medium transition-all active:scale-[0.97]"
              style={{ background: "#f59e0b15", border: "1px solid #f59e0b30", color: "var(--oh-stat-amber-text)", minHeight: "48px" }}
              data-testid="visiteur-maps-btn"
            >
              <MapPin className="w-4 h-4" />
              Itinéraire
            </a>
          )}
        </div>
      </div>

      {/* Pipeline compact */}
      <div className="rounded-xl p-4" style={{ background: "var(--oh-bg-elevated)", border: "1px solid var(--oh-border)" }}>
        <PipelineStepper statut={dossier.statut} compact />
      </div>

      {/* Visite info */}
      <div className="rounded-xl p-4" style={{ background: "var(--oh-bg-elevated)", border: "1px solid var(--oh-border)" }} data-testid="visiteur-visite-info">
        <div className="flex items-center gap-2 mb-3">
          <Calendar className="w-4 h-4" style={{ color: "var(--oh-hub-operation, var(--oh-stat-amber-text))" }} />
          <h3 className="text-xs font-medium uppercase tracking-wide" style={{ color: "var(--oh-text-muted)" }}>Visite</h3>
        </div>
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <span className="text-sm" style={{ color: "var(--oh-text-muted)" }}>Date</span>
            <span className="text-sm font-medium" style={{ color: "var(--oh-text)" }}>{dossier.date_visite || "Non planifiée"}</span>
          </div>
          {dossier.statut_rdv && (
            <div className="flex justify-between items-center">
              <span className="text-sm" style={{ color: "var(--oh-text-muted)" }}>Statut RDV</span>
              <Badge className="text-xs border-0" style={{
                background: dossier.statut_rdv === "effectue" ? "var(--oh-stat-green-bg)" : dossier.statut_rdv === "planifie" ? "var(--oh-stat-blue-bg)" : "var(--oh-stat-slate-bg)",
                color: dossier.statut_rdv === "effectue" ? "var(--oh-stat-green-text)" : dossier.statut_rdv === "planifie" ? "var(--oh-stat-blue-text)" : "var(--oh-stat-slate-text)",
              }}>{dossier.statut_rdv}</Badge>
            </div>
          )}
          <div className="flex justify-between items-center">
            <span className="text-sm" style={{ color: "var(--oh-text-muted)" }}>Type projet</span>
            <span className="text-sm" style={{ color: "var(--oh-text)" }}>{dossier.type_projet || "—"}</span>
          </div>
        </div>
      </div>

      {/* Checklist rapide */}
      <div className="rounded-xl p-4" style={{ background: "var(--oh-bg-elevated)", border: "1px solid var(--oh-border)" }} data-testid="visiteur-checklist">
        <h3 className="text-xs font-medium uppercase tracking-wide mb-2" style={{ color: "var(--oh-text-muted)" }}>Checklist visite</h3>
        {checklist.map((c) => <StatusChip key={c.label} label={c.label} ok={c.ok} />)}
      </div>

      {/* Action Buttons */}
      <div className="space-y-3" data-testid="visiteur-actions">
        <BigActionButton
          icon={Camera}
          label="Photos visite"
          sublabel="Ajouter ou consulter les photos"
          color="var(--oh-stat-amber-text)"
          onClick={() => setActivePanel(activePanel === "photos" ? null : "photos")}
          testId="visiteur-photos-action"
        />
        <BigActionButton
          icon={FolderOpen}
          label="Documents"
          sublabel={`${documents?.length || 0} document(s)`}
          color="var(--oh-stat-blue-text)"
          onClick={() => setActivePanel(activePanel === "documents" ? null : "documents")}
          testId="visiteur-documents-action"
        />
        <BigActionButton
          icon={Shield}
          label="DAL — Diagnostic"
          sublabel="Remplir le diagnostic d'autonomie"
          color="#8b5cf6"
          onClick={() => setActivePanel(activePanel === "dal" ? null : "dal")}
          testId="visiteur-dal-action"
        />
      </div>

      {/* Panels */}
      {activePanel === "photos" && (
        <div className="rounded-xl p-4" style={{ background: "var(--oh-bg-elevated)", border: "1px solid #f59e0b30" }} data-testid="visiteur-photos-panel">
          <PhotosForm dossierId={dossierId} tenantId={tenantId} verrouille={dossier.verrouille} onUpdate={onUpdate} clientNom={dossier.client_nom} dossierRef={dossier.référence} />
        </div>
      )}

      {activePanel === "documents" && (
        <div className="rounded-xl p-4" style={{ background: "var(--oh-bg-elevated)", border: "1px solid #3b82f630" }} data-testid="visiteur-documents-panel">
          <DocumentsForm dossierId={dossierId} tenantId={tenantId} documents={documents || []} verrouille={dossier.verrouille} onUpdate={onUpdate} clientNom={dossier.client_nom} dossierRef={dossier.référence} />
        </div>
      )}

      {activePanel === "dal" && (
        <div className="rounded-xl p-4" style={{ background: "var(--oh-bg-elevated)", border: "1px solid #8b5cf630" }} data-testid="visiteur-dal-panel">
          <div className="flex justify-end mb-2">
            <QRCodeDAL dossierId={dossierId} tenantId={tenantId} />
          </div>
          <DALForm dossierId={dossierId} reference={dossier?.reference} tenantId={tenantId} client={dossier?.client} infosLogement={dossier?.infos_logement} onUpdate={onUpdate} />
        </div>
      )}
    </div>
  );
}
